// React-Screen CPosBalancing ("Kassa oeffnen/abschliessen/Entnahme/Einlage/
// Wechselgeld/uebergeben") - Strangler Phase 5 (entschalt).
// Runbook: tools/REACT-STRANGLER.md.
//
// ENTSCHALT am 23.8.2026 - der letzte Screen der Warteschlange. Sein Kern
// ist die Zaehltabelle: 9 Zeilen x 4 Spalten, abwechselnd ein Geldschein-
// bzw. Muenzbild und ein beschriftetes Eingabefeld, dazu in Zeile 8 die
// Summe. Im Legacy baute addEntities() diese Tabelle Zelle fuer Zelle; hier
// rechnet dieselbe Formel, welche Zelle was bekommt, und React rendert sie.
//
// Die 56 Methoden mit der Geldlogik (Soll-Stand, Uebergabe, Ausdrucke,
// RKSV-relevante Abschluesse) bleiben ueber spec.legacyMethods WOERTLICH
// uebernommen - hier wird KEINE Zeile Rechenlogik angefasst.
//
// Zwei Eigenheiten, die nachgebaut und nicht bereinigt sind:
//  - Der 16. Eintrag ("Gutschein-summe:") sitzt in Zeile 6, Spalte 3 und
//    traegt eigene Styles; sein Bild ist ein LEERES src.
//  - Die Zeile darunter nimmt in denselben beiden Spalten "Summe:" und den
//    Betrag auf; die letzte Zeile hat rechts zwei leere Zellen.
//
// Der Galerie-Eintrag 18 vergleicht diesen Screen OHNE Maske - Bild und DOM
// decken ihn also vollstaendig ab (im Modus "out"). Die uebrigen Modi
// unterscheiden sich nur in Titel und drei Sichtbarkeiten.
import { defineReactScreen } from '../bridge.js';
import { CPushButton } from '../../components/pushbutton.js';
import { CImage } from '../../components/image.js';
import { Caption, ContainerTable, LabeledInputField, NumPad,
         verdrahteLabeledInputField } from '../widgets.jsx';

// Die Bilder der Zaehltabelle, in der Reihenfolge von addEntities().
const GELDBILDER = [
  'lib/img/currency/EUR/500.png', 'lib/img/currency/EUR/200.png',
  'lib/img/currency/EUR/100.png', 'lib/img/currency/EUR/50.png',
  'lib/img/currency/EUR/20.png', 'lib/img/currency/EUR/10.png',
  'lib/img/currency/EUR/5.png', 'lib/img/currency/EUR/2.png',
  'lib/img/currency/EUR/1.png', 'lib/img/currency/EUR/0.50.png',
  'lib/img/currency/EUR/0.20.png', 'lib/img/currency/EUR/0.10.png',
  'lib/img/currency/EUR/0.05.png', 'lib/img/currency/EUR/0.02.png',
  'lib/img/currency/EUR/0.01.png', '',
];
const JE_SPALTE = 9;
const TITEL = {
  start: 'OPEN_BALANCE', end: 'CLOSE_BALANCE', out: 'TAKE_OUT_MONEY',
  in: 'PUT_IN_MONEY', change: 'CHANGE_MONEY_OUT', transfer: 'TRANSFER_BALANCE',
};
const BILDKNOPF = (bild, rechts) => ({
  position: 'absolute', right: rechts, top: '0px', width: '4%', height: '88%',
  backgroundImage: `url(${bild})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
});

function CPosBalancingScreen({ bridge }) {
  const t = bridge.t;
  const modus = bridge.mode;
  const abschluss = modus === 'end' || modus === 'transfer';

  const knopf = (pfad, style, handler, text) => (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: { onMousedown: handler, onMouseout: true, onMouseup: true },
           attributes: { onMousedown_parameter_0: bridge.id },
           proto: CPushButton.prototype,
         })}
         className="CPushButton" style={style}>
      <p {...bridge.el(`${pfad}:0`, 'CCaption')} className={'CCaption ' + (text || 'undefined')}
         dangerouslySetInnerHTML={{ __html: text || '' }} />
    </div>
  );

  // Zaehltabelle: dieselbe Zuordnung wie im Legacy-addEntities.
  //   c = ceil((i+1)/9)*2-1  ->  Spalte des Eingabefeldes (1 oder 3)
  //   r = i bzw. i-9         ->  Zeile
  const zellen = {};   // "r:c" -> Inhalt
  for (let i = 0; i < GELDBILDER.length; i++) {
    const c = Math.ceil((i + 1) / JE_SPALTE) * 2 - 1;
    const r = c === 1 ? i : i - JE_SPALTE;
    const gutschein = i === 15;
    zellen[`${r}:${c - 1}`] = { art: 'bild', src: GELDBILDER[i], versetzt: i >= JE_SPALTE };
    zellen[`${r}:${c}`] = { art: 'feld', index: i, gutschein };
  }
  // Summe: eine Zeile unter dem letzten Eintrag, in denselben Spalten.
  zellen['7:2'] = { art: 'summenText' };
  zellen['7:3'] = { art: 'summe' };

  const zeilen = [];
  for (let r = 0; r < JE_SPALTE; r++) {
    const spalten = [];
    for (let c = 0; c < 4; c++) {
      const z = zellen[`${r}:${c}`];
      if (!z) { spalten.push({}); continue; }
      if (z.art === 'bild') {
        spalten.push({
          style: { width: '8vw' },
          inhalt: (sp) => (
            // src ueber die Attribut-Schiene, nicht als React-Prop: der
            // 16. Eintrag ("Gutschein-summe") hat im Legacy ein LEERES src,
            // und ein leerer String wird von React weggelassen. Im DOM steht
            // dort aber src="" - also so.
            <img {...bridge.widget(`${sp}:0`, 'CImage', {
                   attributes: { src: z.src }, proto: CImage.prototype,
                 })}
                 className="CImage"
                 style={{ position: 'relative', width: '8vw', height: '5vh',
                          ...(z.versetzt ? { left: '2.5vw' } : {}) }} />
          ),
        });
      } else if (z.art === 'feld') {
        spalten.push({
          style: { width: '30vw' },
          inhalt: (sp) => (
            <LabeledInputField
              bridge={bridge} pfad={`${sp}:0`}
              style={z.gutschein
                ? { position: 'relative', width: '20vw', right: '7vw' }
                : { position: 'static', width: '20vw' }}
              label={z.gutschein ? 'Gutschein-<br>summe:' : 'x'}
              labelStyle={{ margin: 'auto', marginRight: '2.5vw',
                            fontSize: z.gutschein ? '1.6vh' : '2.8vh',
                            fontWeight: 'bold', marginLeft: '2.0vw' }}
              // Der Umrechnungsfaktor liegt im Legacy auf dem FELD, nicht
              // auf dem Eingabefeld - sumValues() liest ihn dort.
              handleProperties={{ field: { input: { attributes: { fact: bridge.facts[z.index] } } } }}
              feldProps={{
                inputStyle: { position: 'static', margin: 'auto' },
                inputProperties: { changeValueCallback: { cls: bridge, fnc: bridge.sumValues },
                                   OKcallback: { cls: bridge, fnc: bridge.jumpNext } },
                inputAttributes: { fact: bridge.facts[z.index],
                                   onMousedown_parameter_0: bridge.id,
                                   onMousedown_parameter_1: z.index },
              }} />
          ),
        });
      } else if (z.art === 'summenText') {
        spalten.push({
          style: { textAlign: 'right' },
          inhalt: (sp) => <Caption bridge={bridge} pfad={`${sp}:0`} text="Summe:"
                                   style={{ position: 'relative', left: '10vw', fontSize: '3.4vh',
                                            fontWeight: 'bold', top: '6.7vh' }} />,
        });
      } else {
        spalten.push({
          style: { textAlign: 'left' },
          inhalt: (sp) => <Caption bridge={bridge} pfad={`${sp}:0`} text={bridge.state.summe || '0 EUR'}
                                   style={{ position: 'relative', left: '10vw', fontSize: '3.4vh',
                                            fontWeight: 'bold', top: '6.7vh', marginLeft: '2.0vw' }} />,
        });
      }
    }
    zeilen.push(spalten);
  }

  return (
    <>
      {/* titleContainer */}
      <div {...bridge.el('0', 'CFrame')} className="CFrame titlecontainer"
           style={{ position: 'absolute' }}>
        <Caption bridge={bridge} pfad="0:0" text={t('LABELS', TITEL[modus] || 'OPEN_BALANCE')}
                 style={{ position: 'absolute', left: '5px', top: '7px',
                          fontSize: '3.5vh', fontWeight: 'bold' }} />
        {/* Zwischenspeichern nur beim Abschluss, Soll-Stand nur dort und
            beim Oeffnen - sonst unsichtbar (Legacy-prepare). */}
        {knopf('0:1', { ...BILDKNOPF('lib/img/speichern.png', '15%'),
                        ...(abschluss ? {} : { visibility: 'hidden' }) },
               abschluss ? bridge.saveBalanceData : undefined)}
        {knopf('0:2', { ...BILDKNOPF('lib/img/fragen.png', '10%'),
                        ...(abschluss || modus === 'start' ? {} : { visibility: 'hidden' }) },
               abschluss ? bridge.questEnd : (modus === 'start' ? bridge.questStart : undefined))}
        {knopf('0:3', BILDKNOPF('lib/img/OK.png', '5%'), bridge.okKnopf)}
        {knopf('0:4', BILDKNOPF('lib/img/08_X.png', '0px'), bridge.abbrechenKnopf)}
      </div>

      {/* countContainerFrame mit der Zaehltabelle */}
      <div {...bridge.el('1', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', left: '1.5vw', top: '8%', height: '80%', width: '70%' }}>
        <ContainerTable bridge={bridge} pfad="1:0" zeilen={zeilen} />
      </div>

      {/* numPadContainer */}
      <div {...bridge.el('2', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', top: '20%', right: '2%', height: '34%', width: '24%' }}>
        <NumPad bridge={bridge} pfad="2:0" typ="phone" next />
      </div>

      {/* transferContainer: nur beim Uebergeben sichtbar */}
      <div {...bridge.el('3', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', top: '60%', right: '1%', height: '25%', width: '25%',
                    visibility: modus === 'transfer' ? 'visible' : 'hidden' }}>
        <Caption bridge={bridge} pfad="3:0" text={t('LABELS', 'TRANSFER_BALANCE_TO')}
                 style={{ position: 'absolute', left: '6px', top: '7px',
                          fontSize: '2.5vh', fontWeight: 'bold' }} />
        <Caption bridge={bridge} pfad="3:1" text={t('LABELS', 'PERSONAL_NUMBER')}
                 style={{ position: 'absolute', left: '6px', top: '20%',
                          fontSize: '2.5vh', fontWeight: 'normal' }} />
        {/* cardNumber: nach dem Kartenscan (getPersonalCard) steht hier die
            Personalnummer rot und fett - wie im Legacy, das die Caption per
            properties.styles/value + reDraw umbaute. */}
        {bridge.state.uebergabeKarte == null
          ? <Caption bridge={bridge} pfad="3:2" text={t('LABELS', 'PLEASE_SCAN_CARD')}
                     style={{ position: 'absolute', left: '6px', top: '45%',
                              fontSize: '1.7vh', fontWeight: 'normal' }} />
          : <Caption bridge={bridge} pfad="3:2" text={String(bridge.state.uebergabeKarte)}
                     style={{ position: 'absolute', left: '82px', top: '85px', fontSize: '3.8vh',
                              fontWeight: 'bold', color: '#EE0000' }} />}
        <LabeledInputField bridge={bridge} pfad="3:3" label="PIN:"
                           style={{ width: '95%', bottom: '3px', left: '5px' }}
                           feldProps={{
                             style: { width: '90%' },
                             inputStyle: { position: 'static', margin: 'auto', width: '90%' },
                             inputAttributes: { type: 'password' },
                           }} />
      </div>

      {/* Anzahl stornierter Bons - nur beim Abschluss gefuellt */}
      <Caption bridge={bridge} pfad="4" text={bridge.state.stornoText || ' '}
               style={{ position: 'absolute', bottom: '7%', left: '39%',
                        fontSize: '2.0vh', fontWeight: 'bold' }} />
    </>
  );
}

const legacy = {};


legacy.prepare = function() {

    var defret = jQuery.Deferred();
    var def = jQuery.Deferred();

    var me = this;

    log.add(4, "Info", "Entering balances with mode '" + this.mode + "'", "");

    this.Main = POS.sessions.getSessionComponent();

    // Titel, Sichtbarkeiten und die Verdrahtung der vier Kopfknoepfe
    // entscheidet jetzt die JSX anhand von bridge.mode (okKnopf/
    // abbrechenKnopf verteilen von dort auf die Modus-Methoden). Hier bleibt
    // nur, was KEIN DOM ist.
    if (this.mode == "end" || this.mode == "transfer") {
        $.when(this.getCancelledBons()).done(function(nrcancelledbons) {
            me.update({
                stornoText: POS.lang.getText("LABELS", "NUMBER_CANCELLED_BONS") +
                    "<span style='margin-left:8px; color:#EE0000; font-size:20px'>" +
                    nrcancelledbons + "</span>",
            });
            def.resolve();
        });
    } else {
        def.resolve();
    }

    $.when(def).done(function() {
        me.indexInput = 0;

        me.Main.disableInputFocus();

        if (!POS.Cashbox.isDrawerOpen())
            POS.Cashbox.openDrawer();
        POS.Cashbox.canBeOpen = true;
        POS.Cashbox.countdown = 500;

        me.prepared = true;
        defret.resolve();
    });

    return defret.promise();
}

// Die vier Kopfknoepfe bekamen im Legacy je nach Modus einen anderen
// Handler. Weil die JSX nur EINEN Handler pro Knopf kennt, verteilen diese
// beiden Weichen - die Zuordnung ist woertlich die aus dem alten
// prepare()-switch.
legacy.okKnopf = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    switch (me.mode) {
        case "start": me.okStart(component, parameters); break;
        case "end":
        case "transfer": me.okEnd(component, parameters); break;
        case "out": me.okOut(component, parameters); break;
        case "in": me.okIn(component, parameters); break;
        case "change": me.okChangeOut(component, parameters); break;
    }
}

legacy.abbrechenKnopf = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    switch (me.mode) {
        case "start": me.preCancelStart(component, parameters); break;
        case "end":
        case "transfer": me.preCancelEnd(component, parameters); break;
        default: me.preCancel(component, parameters);
    }
}
legacy.getPersonalCard = function(loginId) {
    if (this.mode != "transfer")
        return;
    var defret = jQuery.Deferred();
    POS.hourGlass.hide();

    /*privMan.enablePrivilege (privList);*/
    var PosBalancingClass = this;
    window.BalancingDomain.getUserIdByLogin(loginId).then(function(rows) {
        //if (r.recordCount() == 0)
        if (rows.length == 0) {
            //r.free();
            var text = POS.lang.getText("MESSAGES", "UNKNOWN_ID_CARD");
            POS.message.show(text, "warning", "OK");
            return;
        } else {
            var userId = parseFloat(rows[0].U_ID);

            /* userId == this.Main.UID || */
            if (!POS.sessions.hasOpenSession(userId)) {
                var text = POS.lang.getText("MESSAGES", "ANOTHER_USER");
                POS.message.show(text, "warning", "OK");
                return;
            }
            if (POS.sessions.openSessions[userId].userProps["POP"] == 0) {
                var text = POS.lang.getText("MESSAGES", "TRANSFER_USER_NOT_ALLOWED");
                POS.message.show(text, "warning", "OK");
                return;
            }
        }

        var children = PosBalancingClass.components.transferContainer.components;

        children.cardNumber.properties.styles.fontSize = "3.8vh";
        children.cardNumber.properties.styles.left = "82px";
        children.cardNumber.properties.styles.top = "85px";
        children.cardNumber.properties.styles.color = "#EE0000";
        children.cardNumber.properties.styles.fontWeight = "bold";
        children.cardNumber.properties.value = loginId;
        children.cardNumber.reDraw();

        PosBalancingClass.components.transferContainer.components.pin.field.input.setFocus();
        defret.resolve();
    });
    return defret.promise();
}

legacy.jumpNext = function(cls) {
    if (cls.indexInput != cls.numberOfEntries - 1) {
        cls.indexInput++;
        cls.inputs[cls.indexInput].field.input.setFocus();
    }
}

legacy.updateFocus = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.indexInput = parameters[1];
}

// (legacy.addEntities ist am 23.8.2026 entfallen: die Zaehltabelle baut
// jetzt die JSX, mit derselben Formel fuer "welcher Eintrag in welche
// Zelle". Die Verweise inputs[]/images[]/sum stellt mountLegacy her. Der
// Originalcode steht in der Git-Historie.)
legacy.preCancel = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.cancel(me);
}

legacy.cancel = function(me) {
    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.cancel);
        else
		setTimeout(function(){ me.cancel(me); }, 1000);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();
    me.removeComponent();
}

legacy.preCancelStart = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.cancelStart(me);
}

legacy.cancelStart = function(me) {
    var text = POS.lang.getText("MESSAGES", "CONF_OPEN_CASHBOX_CANCEL");
    POS.message.show(text, "question", "YESNO", me, me.doCancelStart);
}

legacy.doCancelStart = function(me, result) {
    if (result == "YES") {
        POS.isPOSBeingOpened = false;
        me.removeComponent();
    }

    POS.Cashbox.countdown = -1; // let the drawer open
}

legacy.preCancelEnd = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.cancelEnd(me);
}

legacy.cancelEnd = function(me) {
    var dataPresent = false;

    for (var i in me.inputs) {
        if (me.inputs[i].field.input.properties.attributes.value != "") {
            dataPresent = true;
            break;
        }
    }

    if (dataPresent)
        var text = POS.lang.getText("MESSAGES", "CONF_CLOSE_CASHBOX_CANCEL_WITH_DATA");
    else
        var text = POS.lang.getText("MESSAGES", "CONF_CLOSE_CASHBOX_CANCEL");
    POS.message.show(text, "question", "YESNO", me, me.doCancelEnd);
}

legacy.doCancelEnd = function(me, result) {
    if (result == "NO")
        return;

    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        window.text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doCancelEnd);
        else
		setTimeout(function(){ me.doCancelEnd(me); }, 1000);
           // me.doCancelEnd(me);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();
    POS.isPOSBeingClosed = false;
    me.removeComponent();
}

legacy.questStart = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var startSum = fix2Decimals(me.getSum());

    var percent = parseFloat(POS.EnvProperties.BALANCEERRORMARGIN);
    $.when(me.getLastSum()).then(function(sumPCR) {
        if (!sumPCR)
            sumPCR = 0;
        if (sumPCR == 0) {
            var text = POS.lang.getText("MESSAGES", "OPEN_SUM_RIGHT");
            POS.message.show(text, "info", "OK");
            me.count = 3;
            return;
        }
        sdump(sumPCR);

        var sumPOPMin = sumPCR - (sumPCR * (percent / 100));
        var sumPOPMax = sumPCR + (sumPCR * (percent / 100));

        if (startSum > sumPOPMin && startSum < sumPOPMax) {
            var text = POS.lang.getText("MESSAGES", "OPEN_SUM_RIGHT");
            POS.message.show(text, "info", "OK");
            me.count = 3;
            return;
        } else {
            me.count--;

            if (me.count == 0) {
                me.components.titleContainer.components.questButton.setEnabled(false);
                me.saveTimestamp();
            }

            text = POS.lang.getText("MESSAGES", "CASHBOX_SUM_WRONG");
            if (!POS.Cashbox.isDrawerOpen())
                var fnc = POS.Cashbox.openDrawer;
            else
                fnc = null;
            POS.message.show(text, "warning", "OK", null, fnc);
            me.addBalancingValues(me, "POP", -1);
        }
    });
}

legacy.okStart = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var startSum = fix2Decimals(me.getSum());

    var percent = parseFloat(POS.EnvProperties.BALANCEERRORMARGIN);
    $.when(me.getLastSum()).then(function(sumPCR) {
        if (sumPCR == 0) {
            var text = POS.lang.getText("MESSAGES", "CONF_OPEN_CASHBOX");
            POS.message.show(text, "question", "YESNO", me, me.doOkStart);
            return;
        }
        log.add(4, "Info", "Opening POS: amount should be " + sumPCR);
        log.add(4, "Info", "Opening POS: amount given is " + startSum);
        sdump(sumPCR);

        var sumPOPMin = sumPCR - (sumPCR * (percent / 100));
        var sumPOPMax = sumPCR + (sumPCR * (percent / 100));

        if (startSum > sumPOPMin && startSum < sumPOPMax)
            var text = POS.lang.getText("MESSAGES", "CONF_OPEN_CASHBOX");
        else
            text = POS.lang.getText("MESSAGES", "CONF_OPEN_CASHBOX_FALSE");

        POS.message.show(text, "question", "YESNO", me, me.doOkStart);
    });
}

legacy.doOkStart = function(me, result) {
    if (result == "NO")
        return;

    me.count = 3;
    //fs.fileName = me.fileName;
    fs.deleteFile(me.fileName);

    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doOkStart);
        else
	setTimeout(function(){ me.doOkStart(me); }, 1000);
            //me.doOkStart(me);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();

    $.when(me.addBalancingValues(me, "POP", 0)).then(function(idBon) {
        $.when(me.printBalance(me, idBon, "POP", null)).done(function() {

            POS.isPOSBeingOpened = false;
            POS.isPOSOpened = true;
            me.removeComponent();
            POS.sessions.enableAllPOSSessions();
        });
    });
}

legacy.testTransferPIN = function(loginId) {

    var PosBalancingClass = this;
    var dfd = jQuery.Deferred();
    if (!loginId)
        loginId = 0;
    window.BalancingDomain.getTransferUser(loginId).then(function(rows) {
        var dbUserId = rows[0].U_ID;
        var dbUserName = rows[0].Name;
        var dbMD5Pass = rows[0].MD5Pass;
        var inputPIN = PosBalancingClass.components.transferContainer.components.pin.field.input.properties.attributes.value;

        var MD5PIN = hex_md5(inputPIN);

        //r.free();

        if (dbMD5Pass == MD5PIN) {
            PosBalancingClass.transferUserLogin = loginId;
            PosBalancingClass.transferUserName = dbUserName;
            dfd.resolve(true);
            return;
            //return true;
        } else {
            //return false;
            dfd.resolve(false);
            return;
        }
    });
    return dfd.promise();
}

legacy.questEnd = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (!POS.isPOSOpened) {
        var text = POS.lang.getText("MESSAGES", "POS_ALREADY_CLOSED");
        POS.message.show(text, "warning", "OK");
        return;
    }

    var endSum = fix2Decimals(me.getSum());

    var percent = parseFloat(POS.EnvProperties.BALANCEERRORMARGIN);
    $.when(me.getLastSum()).then(function(sumPOP) {
        $.when(me.getIncomeSession(false)).then(function(incomeSession) {
            //var incomeSession = me.getIncomeSession(false);
            var allMoneySession = sumPOP + incomeSession;

            var sumPCCMin = allMoneySession - (allMoneySession * (percent / 100));
            var sumPCCMax = allMoneySession + (allMoneySession * (percent / 100));
            sdump(allMoneySession);

            if (endSum >= sumPCCMin && endSum <= sumPCCMax) {
                var text = POS.lang.getText("MESSAGES", "CLOSE_SUM_RIGHT");
                POS.message.show(text, "info", "OK");
                me.count = 3;
                return;
            } else {
                me.count--;

                if (me.count == 0) {
                    me.components.titleContainer.components.questButton.setEnabled(false);
                    me.saveTimestamp();
                }

                text = POS.lang.getText("MESSAGES", "CASHBOX_SUM_WRONG");
                if (!POS.Cashbox.isDrawerOpen())
                    var fnc = POS.Cashbox.openDrawer;
                else
                    fnc = null;
                POS.message.show(text, "warning", "OK", null, fnc);
                me.addBalancingValues(me, "PCC", -1);
            }
        });
    });
}

legacy.okEnd = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (!POS.isPOSOpened) {
        var text = POS.lang.getText("MESSAGES", "POS_ALREADY_CLOSED");
        POS.message.show(text, "warning", "OK");
        return;
    }

    if (me.mode == "transfer") {
        var loginId = me.components.transferContainer.components.cardNumber.properties.value;

        if (isNaN(parseInt(loginId))) {
            var text = POS.lang.getText("MESSAGES", "ANOTHER_USER");
            POS.message.show(text, "warning", "OK");
            return;
        }

        /*
        if (! me.testTransferPIN(loginId)){
        text = POS.lang.getText("MESSAGES", "PIN_WRONG");
        POS.message.show (text, "warning", "OK");
        return;
        }*/

    }

    $.when(me.testTransferPIN(loginId)).then(function(val) {
        //testTransferPIN=val;
        if (me.mode == "transfer") {
            if (!val) {
                text = POS.lang.getText("MESSAGES", "PIN_WRONG");
                POS.message.show(text, "warning", "OK");
                return;
            }
        }

        var endSum = fix2Decimals(me.getSum());

        var percent = parseFloat(POS.EnvProperties.BALANCEERRORMARGIN);
        $.when(me.getLastSum()).then(function(sumPOP) {
            $.when(me.getIncomeSession(false)).then(function(incomeSession) {

                var allMoneySession = sumPOP + incomeSession;

                var sumPCCMin = allMoneySession - (allMoneySession * (percent / 100));
                var sumPCCMax = allMoneySession + (allMoneySession * (percent / 100));

                log.add(4, "Info", "Closing POS: amount should be " + allMoneySession);
                log.add(4, "Info", "Closing POS: amount given is " + endSum);
                sdump(allMoneySession);

                if (endSum >= sumPCCMin && endSum <= sumPCCMax)
                    var text = POS.lang.getText("MESSAGES", "CONF_CLOSE_CASHBOX");
                else
                    text = POS.lang.getText("MESSAGES", "CONF_CLOSE_CASHBOX_FALSE");

                POS.message.show(text, "question", "YESNO", me, me.doOkEnd);
            });
        });
    });
}

legacy.doOkEnd = function(me, result) {
    if (result != "YES")
        return;

    me.components.titleContainer.components.okButton.setEnabled(false);

    me.count = 3;
    //fs.fileName = me.fileName;
    fs.deleteFile(me.fileName);

    $.when(me.addBalancingValues(me, "PCC", -1)).then(function(idBon) {
        me.idBon = idBon;
        POS.message.hide();

        me.changeMoney = me.calculatePCR(me);

        $.when(me.printBalance(me, me.idBon, "PCC", null)).done(function() {

            var text = POS.lang.getText("MESSAGES", "TAKE_OUT_MONEY");
            POS.message.show(text, "info", "OK", me, me.doDoOkEnd);

            if (!POS.Cashbox.isDrawerOpen())
                POS.Cashbox.openDrawer()
        });
    });
}

legacy.doDoOkEnd = function(me, result) {
    if (me.mode == "end") {
        try {
            var def = me.Main.cmpList.removeAll();
        } catch (e) {
            log.add(2, "Error", "Could not delete all parked bons... Continuing though!", e);
        }
        var t = "PCR";
    } else if (me.mode == "transfer") {
        var t = "PCT";
        def = 0;
    }
    $.when(def).done(function() {
        $.when(me.printBalance(me, me.idBon, t, me.changeMoney)).done(function() {
            $.when(me.addBalancingValues(me, t, 0)).done(function() {
                $.when(me.releaseLastPCC()).done(function() {

                    POS.isPOSBeingClosed = false;

                    var text = POS.lang.getText("MESSAGES", "PLEASE_WAIT");
                    POS.hourGlass.show(text, me, me.finishOkEnd);
                });
            });
        });
    })
}

legacy.finishOkEnd = function(me) {
    if (me.mode == "end") {
        POS.isPOSOpened = false;
        POS.sessions.disableAllPOSSessions();
    }

    me.Main.BonManager.reset();
    me.removeComponent();

    if (me.mode == "transfer") {
        POS.Cashbox.canBeOpen = false;
        POS.Cashbox.resetCountdown();
    } else if (me.mode == "end")
        POS.Cashbox.countdown = -1;

    POS.hourGlass.hide();
}

legacy.okOut = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    me.doOkOut(me);
}

legacy.doOkOut = function(me) {

    $.when(me.getLastSum()).then(function(lastSum) {
        $.when(me.getIncomeSession(false)).then(function(IncomeSession) {
            var allMoneySession = lastSum + IncomeSession;

            var endSum = fix2Decimals(me.getSum());

            if (endSum == 0) {
                var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
                POS.message.show(text, "info", "OK");
                return;
            }

            if (endSum > allMoneySession) {
                var text = POS.lang.getText("MESSAGES", "MORE_MONEY_THAN_CASHBOX");
                POS.message.show(text, "info", "OK");
                return;
            }

            POS.Cashbox.canBeOpen = false;

            if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
                var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
                if (!POS.hourGlass.isActive())
                    POS.hourGlass.show(text, me, me.doOkOut);
                else
			setTimeout(function(){ me.doOkOut(me); }, 1000);			             
                return;
            }

            POS.Cashbox.resetCountdown();
            POS.hourGlass.hide();

            $.when(me.addBalancingValues(me, "MOU", 0)).then(function(idBon) {
                $.when(me.printBalance(me, idBon, "MOU", null)).done(function() {
                    me.removeComponent();
                });
            });
        });
    });
}

legacy.okIn = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    me.doOkIn(me);
}

legacy.doOkIn = function(me) {
    POS.Cashbox.canBeOpen = false;
    if (me.getSum() == 0) {
        var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
        POS.message.show(text, "info", "OK");
        return;
    }



    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doOkIn);
        else
		setTimeout(function(){ me.doOkIn(me); }, 1000);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();

    $.when(me.addBalancingValues(me, "MIN", 0)).then(function(idBon) {
        $.when(me.printBalance(me, idBon, "MIN", null)).done(function() {
            me.removeComponent();
        });
    });
}

legacy.okChangeOut = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.getSum() == 0) {
        var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
        POS.message.show(text, "info", "OK");

        return;
    }

    $.when(me.addBalancingValues(me, "MCO", 0)).then(function(idBon) {
        me.lastIdBonMCO = idBon;
        $.when(me.printBalance(me, me.lastIdBonMCO, "MCO", null)).done(function() {

            me.sumChangeOut = me.getSum();

            me.cleanForm();

            me.components.titleContainer.components.titleText.properties.
            value = POS.lang.getText("LABELS", "CHANGE_MONEY_IN");
            me.components.titleContainer.components.titleText.reValue();
            me.components.titleContainer.components.okButton.properties.events.
            onMousedown = me.okChangeIn;
            me.components.titleContainer.components.cancelButton.properties.events.
            onMousedown = me.cancelChange;
        });
    });
}

legacy.okChangeIn = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var sumChangeIn = me.getSum();

    if (me.sumChangeOut != sumChangeIn) {
        var text = POS.lang.getText("MESSAGES", "CHANGE_SUM_WRONG");
        if (!POS.Cashbox.isDrawerOpen())
            var fnc = POS.Cashbox.openDrawer;
        else
            fnc = null;
        POS.message.show(text, "warning", "OK", null, fnc);

        return;
    }

    me.doOkChangeIn(me);
}

legacy.doOkChangeIn = function(me) {
    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doOkChangeIn);
        else
	setTimeout(function(){ me.doOkChangeIn(me); }, 1000);        
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();

    $.when(me.addBalancingValues(me, "MCI", 0)).then(function(idBon) {
        $.when(me.printBalance(me, idBon, "MCI", null)).done(function() {

            me.removeComponent();
        });
    });
}

legacy.cancelChange = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var text = POS.lang.getText("MESSAGES", "CASH_CHANGE_CANCEL");
    POS.message.show(text, "question", "YESNO", me, me.doCancelChange);
}

legacy.doCancelChange = function(me, result) {
    if (result != "YES")
        return;

    var text = POS.lang.getText("MESSAGES", "CASH_CHANGE_RETURN");
    POS.message.show(text, "info", "OK", me, me.doDoCancelChange);
}

legacy.doDoCancelChange = function(me) {
    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doDoCancelChange);
        else
	    setTimeout(function(){ me.doDoCancelChange(me); }, 1000);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();

    me.restoreFormValues();

    $.when(me.cancelLastChangeOut(me.lastIdBonMCO)).done(function() {
        $.when(me.addBalancingValues(me, "MCI", 0)).then(function(idBon) {
            $.when(me.printBalance(me, idBon, "MCI", null)).done(function() {
                me.removeComponent();
            });
        });
    });
}

legacy.cleanForm = function() {
    this.lastMoneyInCash = Array();

    for (window.i in this.inputs) {
        this.inputs[i].field.input.gotClear();
        this.lastMoneyInCash[i] = this.inputs[i].field.input.properties.attributes.value;
    }
}

legacy.restoreFormValues = function() {
    for (window.i in this.inputs)
        this.inputs[i].field.input.properties.attributes.value = this.lastMoneyInCash[i];
}

legacy.getSum = function() {
    var sum = this.sum.properties.value.replace(" EUR", "");
    sum = sum.replace(".", "");
    sum = sum.replace(",", ".");
    sum = parseFloat(sum);

    return sum;
}

legacy.sumValues = function(cls, noDraw) {
    var sum = 0;

    for (var i in cls.inputs) {
        if (cls.inputs[i].field.input.properties.attributes.value != "") {
            var value = new String(cls.inputs[i].field.input.properties.attributes.value);
            value = value.replace(",", ".");
            value = parseFloat(value);
        } else
            value = 0;

        var fact = parseFloat(cls.inputs[i].properties.field.input.attributes.fact)

        sum += value * fact;
    }

    sum += cls.prevCardBalances;

    sum = fix2Decimals(sum);
    cls.sum.properties.value = float2formatString(sum) + " EUR";
    if (sum > POS.EnvProperties.MAXBALANCEAMOUNT)
        cls.sum.properties.value = "Fehler!";
    if (!noDraw)
        cls.sum.reDraw();
}

// Logik in frontend/src/domain/balancing.js; Deferred-Signatur erhalten.
legacy.insertBONETransaction = function(idBon, amount, payMode, date) {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.insertBONETransaction(idBon, amount, payMode, date).then(function() {
        dfd.resolve();
    });
    return dfd.promise();
}

// Sammelt die Zaehlfeld-Werte aus dem Formular und laesst die Domaene den
// kompletten Balancing-Bon schreiben (BON + BONE-Zeilen + PCC-Summenzeilen)
// - Logik in frontend/src/domain/balancing.js, Verhalten identisch zum
// frueheren Callback-Iterator hier.
legacy.addBalancingValues = function(me, type, cancelled) {
    var dfd = jQuery.Deferred();
    if (type == "PCT") {
        var userLogin = me.transferUserLogin;
        var userName = me.transferUserName;
    } else {
        userLogin = me.Main.userProps.Login;
        userName = me.Main.userProps.Name;
    }

    if (type != "PCR" && type != "PCT") {
        me.lastMoneyInCash = Array();
        for (var c in me.inputs) {
            var val = parseFloat(me.inputs[c].field.input.properties.attributes.value);
            me.lastMoneyInCash[c] = isNaN(val) ? 0 : val;
        }
    }

    var entries = new Array();
    var inpts = Object.keys(me.inputs);
    for (var j = 0; j < me.inputs.length; j++) {
        var i = inpts[j];
        var unitAmount = parseFloat(me.inputs[i].properties.field.input.attributes.fact);

        if (i < 7)
            var quantity = me.inputs[i].field.input.properties.attributes.value;
        else
            quantity = me.lastMoneyInCash[i];

        if (quantity == "")
            quantity = 0;
        else
            quantity = parseFloat(quantity);

        var amount = fix2Decimals(unitAmount * quantity);

        switch (parseInt(i)) {
            case me.numberOfEntries - 1:
                var unit = "VCR";
                break;
            default:
                unit = "BAR";
        }

        entries.push({ quantity: quantity, amount: amount, unitAmount: unitAmount, unit: unit });
    }

    window.BalancingDomain.insertBalancingBon(type, userLogin, userName, cancelled, entries).then(function(idBon) {
        dfd.resolve(idBon);
    });
    return dfd.promise();
}

legacy.releaseLastPCC = function() {
    var defret = jQuery.Deferred();
    window.BalancingDomain.releaseLastPCC().then(function() {
        defret.resolve();
    });
    return defret.promise();
}

legacy.printBalance = function(me, idBon, type, change) {
    window.Printer = new CPrinter();
    var defret = jQuery.Deferred();
    $.when(Printer.head()).then(function() {
        Printer.addLine("A", 1, false, 0);

        switch (type) {
            case "POP":
                var title = POS.lang.getText("LABELS", "OPEN_BALANCE_PRINT");
                break;
            case "PCC":
                title = POS.lang.getText("LABELS", "CHANGE_MONEY");
                break;
            case "PCR":
                title = POS.lang.getText("LABELS", "CLOSE_BALANCE_PRINT");
                break;
            case "PCT":
                title = POS.lang.getText("LABELS", "TRANSFER_BALANCE_PRINT");
                break;
            case "MOU":
                title = POS.lang.getText("LABELS", "TAKE_OUT_MONEY");
                break;
            case "MIN":
                title = POS.lang.getText("LABELS", "PUT_IN_MONEY");
                break;
            case "MCI":
                title = POS.lang.getText("LABELS", "CHANGE_MONEY_IN");
                break;
            case "MCO":
                title = POS.lang.getText("LABELS", "CHANGE_MONEY_OUT");
                break;
        }
        Printer.addElement(title, 20, 2, true, 0, 41);

        if (me.mode == "transfer" && type == "PCC") {
            Printer.addLine("B", 1, false, 5);
            Printer.addLine("B", 1, false, 0);
            window.text = POS.lang.getText("LABELS", "CONFIRMED_BY");
            Printer.addElement(text, 27, 2, false, 0, 55);
            Printer.addLine("B", 1, false, 0);
            window.text = me.transferUserName;
            Printer.addElement(text, 27, 2, false, 0, 55);
        }

        Printer.addDivisionLine();

        var totalAmount = 0;

        var ColLimits = new Array();
        ColLimits[0] = 7;
        ColLimits[1] = 9;
        ColLimits[2] = 24;
        ColLimits[3] = 29;
        ColLimits[4] = 42;

        for (var i in me.inputs) {
            var unitAmount = parseFloat(me.inputs[i].properties.field.input.attributes.fact);

            if (type == "PCR" || type == "PCT")
                quantity = change[i];
            else // POP, PCC, MOU, MIN, MCI, MCO
            {
                var quantity = me.inputs[i].field.input.properties.attributes.value;

                if (quantity == "")
                    quantity = 0;
                else
                    quantity = parseFloat(quantity);
            }

            var amount = unitAmount * quantity;
            totalAmount += amount;

            if (type == "PCR" || type == "PCT") {
                if (i == 15) {
                    var amountVCR = amount;
                    var quantityVCR = quantity;
                }
                if (i > 6)
                    continue;
            }

            unitAmount = float2formatString(unitAmount) + " &euro;";
            amount = float2StringD2(amount) + " &euro;";

            switch (i) {
                /*            case me.numberOfEntries - 2:
                if (type == "PCR"){
                Printer.addLine("A", 1, false, 0);
                Printer.addElement(POS.lang.getText("LABELS", "CREDIT_CARDS"), 0, 0, false, 0, ColLimits[2]-1);
                Printer.addElement("=", ColLimits[3]-1, 1, false, ColLimits[2], ColLimits[3]-1);
                Printer.addElement(amount, ColLimits[4]-1, 1, false, ColLimits[3], ColLimits[4]-1);
                }
                break;*/
                case "15":
                    /*
                    Printer.addLine("A", 2, false, 0);
                    Printer.addElement(quantity, 0, 0, true, 0, ColLimits[0]-1);
                    Printer.addElement("x", ColLimits[1]-1, 1, false, ColLimits[0], ColLimits[1]-1);
                    Printer.addElement(POS.lang.getText("LABELS", "VOUCHERS"), ColLimits[2]-1, 1, true, ColLimits[1], ColLimits[2]-1);
                    Printer.addElement("=", ColLimits[3]-1, 1, false, ColLimits[2], ColLimits[3]-1);
                    Printer.addElement(amount, ColLimits[4]-1, 1, true, ColLimits[3], ColLimits[4]-1);
                     */
                    break;
                default:
                    Printer.addLine("A", 2, false, 0);
                    Printer.addElement(quantity, 0, 0, true, 0, ColLimits[0] - 1);
                    Printer.addElement("x", ColLimits[1] - 1, 1, false, ColLimits[0], ColLimits[1] - 1);
                    Printer.addElement(unitAmount, ColLimits[2] - 1, 1, true, ColLimits[1], ColLimits[2] - 1);
                    Printer.addElement("=", ColLimits[3] - 1, 1, false, ColLimits[2], ColLimits[3] - 1);
                    Printer.addElement(amount, ColLimits[4] - 1, 1, true, ColLimits[3], ColLimits[4] - 1);
            }
        }

        Printer.addDivisionLine();
        //var def1 = 0;
        //var def2 = 0;
        var def3 = $.Deferred();

        if (type == "PCR" || type == "PCT") {
            Printer.addLine("A", 2, false, 0);
            Printer.addElement(POS.lang.getText("LABELS", "CASH") + ":", 0, 0, true, 0, 20);
            Printer.addElement(float2StringD2(totalAmount - amountVCR) + " &euro;", 41, 1, true, 0, 41);

            $.when(me.getIncomeSession(true, "BK")).then(function(sumBK) {
                Printer.addLine("A", 2, false, 0);
                Printer.addElement(POS.lang.getText("LABELS", "DEBITCARD") + ":", 0, 0, true, 0, 20);
                Printer.addElement(float2StringD2(sumBK) + " &euro;", 41, 1, true, 0, 41);

                $.when(me.getIncomeSession(true, "CC")).then(function(sumCC) {
                    Printer.addLine("A", 2, false, 0);
                    Printer.addElement(POS.lang.getText("LABELS", "CREDITCARD") + ":", 0, 0, true, 0, 20);
                    Printer.addElement(float2StringD2(sumCC) + " &euro;", 41, 1, true, 0, 41);

                    Printer.addDivisionLine();

                    var wholeSum = totalAmount + sumBK + sumCC;
                    var wholeSumWithoutVCR = wholeSum - amountVCR;

                    Printer.addLine("A", 2, false, 0);
                    window.text = POS.lang.getText("LABELS", "TOTAL_SUM") + ":";
                    Printer.addElement(text, 0, 0, true, 0, 20);
                    Printer.addElement(float2StringD2(wholeSumWithoutVCR) + " &euro;", 41, 1, true, 0, 41);

                    Printer.addLine("A", 2, false, 0);
                    window.text = POS.lang.getText("LABELS", "VOUCHERS") + ":";

                    Printer.addElement(text, 0, 0, true, 0, 20);
                    Printer.addElement(float2StringD2(amountVCR) + " &euro;", 41, 1, true, 0, 41);

                    Printer.addDivisionLine();

                    $.when(me.getReturnGoodsAmount()).then(function(returnGoodsAmount) {
                        $.when(me.getComplainsAmount()).then(function(complainsAmount) {

                            complainsAmount = Math.abs(complainsAmount);

                            Printer.addLine("A", 1, false, 0);
                            window.text = POS.lang.getText("LABELS", "RETURN_GOODS_AMOUNT") + ":";
                            Printer.addElement(text, 0, 0, false, 0, 20);
                            Printer.addElement(float2StringD2(returnGoodsAmount) + " &euro;", 41, 1, false, 0, 41);

                            Printer.addLine("A", 1, false, 0);
                            window.text = POS.lang.getText("LABELS", "COMPLAINS_AMOUNT") + ":";
                            Printer.addElement(text, 0, 0, false, 0, 20);
                            Printer.addElement(float2StringD2(complainsAmount) + " &euro;", 41, 1, false, 0, 41);
                            def3.resolve();
                        });
                    });
                });
            });
        } else {
            Printer.addLine("A", 2, false, 0);
            Printer.addElement(POS.lang.getText("LABELS", "SUM") + ":", 0, 0, true, 0, 20);
            Printer.addElement(float2StringD2(totalAmount) + " &euro;", 41, 1, true, 0, 41);
            def3.resolve();
        }
        $.when(def3).done(function(v3) {

            Printer.addDivisionLine();

            Printer.addLine("B", 1, false, 0);
            var text = POS.lang.getText("LABELS", "CASHBOX") + " |" +
                POS.lang.getText("LABELS", "RECEIPT_NUMBER") + "| " +
                POS.lang.getText("LABELS", "USER") + " |   " +
                POS.lang.getText("LABELS", "DATE") + "    | " +
                POS.lang.getText("LABELS", "TIME");
            Printer.addElement(text, 4, 0, false, 4, 55);

            Printer.addLine("B", 1, false, 0);
            var now = new Date();
            text = expandInteger(POS.POSId, 5) + " | " + expandInteger(idBon, 9) + " | ";
            text += expandInteger(POS.sessions.getSessionComponent().userProps.Login, 8) + " | " +
                expandInteger(now.getDate(), 2) +
                "." + expandInteger(now.getMonth() + 1, 2) +
                "." + now.getFullYear() + " |  " + expandInteger(now.getHours(), 2) +
                ":" + expandInteger(now.getMinutes(), 2);
            Printer.addElement(text, 4, 0, false, 4, 55);
            Printer.addLine("B", 1, false, 3);

            var userName = me.Main.userProps["Name"];

            Printer.addLine("B", 1, false, 0);
            text = POS.lang.getText("LABELS", "LOGGED_USER_NAME") + " " + userName;
            Printer.addElement(text, 27, 2, false, 0, 55);
            Printer.addLine("B", 1, false, 3);

            Printer.print();
            defret.resolve();
        });
    });
    return defret.promise();
}

legacy.getLastSum = function() {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getLastSum(this.mode).then(function(totalAmount) {
        dfd.resolve(totalAmount);
    });
    return dfd.promise();
}

legacy.calculatePCR = function(me) {
    var referenceMoney = 300;
    var tempSum = 0;
    var finished = false;
    var takeOut = Array();

    for (var i = 7; i <= me.numberOfEntries - 2; i++) {
        takeOut[i] = 0;
        tempSum += me.inputs[i].field.input.properties.attributes.value *
            me.inputs[i].properties.field.input.attributes.fact;
    }

    for (var i = 0; i < 3; i++) {
        takeOut[i] = me.doTakeOut(i);
    }

    for (i = 6; i >= 3; i--) {
        tempSum += me.inputs[i].field.input.properties.attributes.value *
            me.inputs[i].properties.field.input.attributes.fact;
    }

    takeOut[me.numberOfEntries - 1] = me.doTakeOut(me.numberOfEntries - 1);
    //    takeOut[me.numberOfEntries - 2] = me.doTakeOut(me.numberOfEntries - 2);

    for (i = 3; i <= 6; i++) {
        takeOut[i] = 0;
        var qty = me.inputs[i].field.input.properties.attributes.value;
        if (!qty)
            qty = 0;
        var fact = me.inputs[i].properties.field.input.attributes.fact;

        for (var j = 0; j < qty; j++) {
            if (tempSum - fact >= referenceMoney) {
                me.inputs[i].field.input.properties.attributes.value--;
                tempSum -= fact;
                takeOut[i]++;
            }
        }
    }

    for (i = 6; i >= 3; i--) {
        qty = me.inputs[i].field.input.properties.attributes.value;
        if (!qty)
            qty = 0;
        fact = me.inputs[i].properties.field.input.attributes.fact;

        for (j = 0; j < qty; j++) {
            if (tempSum - fact >= referenceMoney) {
                me.inputs[i].field.input.properties.attributes.value--;
                tempSum -= fact;
                takeOut[i]++;
            }
        }
    }

    var fivesIn = parseFloat(me.inputs[6].field.input.properties.attributes.value);
    var tensIn = parseFloat(me.inputs[5].field.input.properties.attributes.value);
    var fivesOut = takeOut[6];
    var tensOut = takeOut[5];
    var anyChange = false;
    while (fivesIn - 2 > 0 && fivesIn - 2 >= tensIn + 1 && tensOut > 0) {
        if (!anyChange)
            anyChange = true;
        fivesIn -= 2;
        tensIn++;
        fivesOut += 2;
        tensOut--;
    }

    if (!anyChange)
        return takeOut;

    takeOut[6] = fivesOut;
    takeOut[5] = tensOut;
    me.inputs[6].field.input.properties.attributes.value = fivesIn;
    me.inputs[5].field.input.properties.attributes.value = tensIn;

    return takeOut;
}

legacy.doTakeOut = function(ind) {
    var takeOut = this.inputs[ind].field.input.properties.attributes.value;
    if (takeOut == "")
        takeOut = 0;
    else
        takeOut = parseFloat(takeOut);
    this.inputs[ind].field.input.properties.attributes.value = 0;

    return takeOut;
}

legacy.getLastPOPTS = function() {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getLastPOPTS().then(function(ts) { dfd.resolve(ts); });
    return dfd.promise();
}

legacy.getSpecialPayments = function(mode) {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getSpecialPayments(mode).then(function(sum) { dfd.resolve(sum); });
    return dfd.promise();
}

legacy.getIncomeSession = function(onlyCards, cardMode) {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getIncomeSession(onlyCards, cardMode).then(function(total) { dfd.resolve(total); });
    return dfd.promise();
}

legacy.removeComponent = function() {
    log.add(4, "Info", "balances window off", "");

    if (!this.saveData)
        POS.tempBalancingValues = new Array();
    else
        this.saveData = false;

    this.removeMe();
    this.Main.enableInputFocus();
}

legacy.saveBalanceData = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.doSaveBalanceData(me);
}

legacy.doSaveBalanceData = function(me) {
    POS.Cashbox.canBeOpen = false;

    if (POS.Cashbox.isDrawerOpen() && POS.EnvProperties.CASHBOX == "YES") {
        var text = POS.lang.getText("MESSAGES", "CLOSE_CASHBOX");
        if (!POS.hourGlass.isActive())
            POS.hourGlass.show(text, me, me.doSaveBalanceData);
        else
		setTimeout(function(){ me.doSaveBalanceData(me); }, 1000);
        return;
    }

    POS.Cashbox.resetCountdown();
    POS.hourGlass.hide();

    POS.tempBalancingValues = new Array();

    for (var i in me.inputs)
        POS.tempBalancingValues.push(me.inputs[i].field.input.properties.attributes.value);

    me.saveData = true;
    me.removeComponent();
}

legacy.saveTimestamp = function() {
    try { 
	var me = this;
        //this.fh.open(true);
        var d = new Date();
	fs.fileName = me.fileName;
        fs.overwrite((d / 1000).toFixed(0));
    } catch (e) {
        log.add(2, "Error", "Timestamp bei Kassa Abschluss konnte nicht gespeichert werden", e);
    }
}

legacy.testTimestamp = function() {
    try {
	var me = this;
	fs.fileName = me.fileName;
    	var timestamp = parseFloat(fs.readAll());

        //this.fh.open(true);
        //this.fh.readOpen();
    } catch (e) {
        log.add(2, "Error", "Folgende Datei konnte nicht gelesen werden: " + this.fileName, e);
        return false;
    }
	fs.fileName = me.fileName;
    var timestamp = parseFloat(fs.readAll());

    if (isNaN(timestamp))
        return true;
    else {
        var d = new Date();
        var now = (d / 1000).toFixed(0);
        if (now - timestamp > this.pause)
            return true;
        else
            return false;
    }
}

legacy.getCancelledBons = function() {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getCancelledBons().then(function(n) { dfd.resolve(n); });
    return dfd.promise();
}

legacy.getReturnGoodsAmount = function() {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getReturnGoodsAmount().then(function(a) { dfd.resolve(a); });
    return dfd.promise();
}

legacy.getComplainsAmount = function() {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.getComplainsAmount().then(function(a) { dfd.resolve(a); });
    return dfd.promise();
}

// Aufrufer nutzen $.when auf dem Rueckgabewert -> Deferred-Signatur erhalten.
legacy.cancelLastChangeOut = function(bonId) {
    var dfd = jQuery.Deferred();
    window.BalancingDomain.cancelLastChangeOut(bonId).then(function() { dfd.resolve(); });
    return dfd.promise();
}



export const CPosBalancing = defineReactScreen({
  className: 'CPosBalancing',
  addclass: 'CPosBalancing',
  legacyName: 'CPosBalancing',
  htmlType: 'div',
  Screen: CPosBalancingScreen,
  // Wurzel-Styles des Legacy-Konstruktors (Reihenfolge unveraendert).
  rootStyles: {
    backgroundColor: '#B0B0B0',
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '0px',
    zIndex: '1',
    top: '0px',
  },
  // Felder des Legacy-Konstruktors - im KONSTRUKTOR, nicht in prepare():
  // CPosBalancing-Aufrufer setzen mode (und bei der Uebergabe weitere
  // Felder) zwischen Konstruktion und draw().
  fields: () => ({
    collector: true,
    xml: null,
    count: 3,
    mode: null,
    countContainer: null,
    inputs: new Array(),
    images: new Array(),
    facts: Array('500', '200', '100', '50', '20', '10', '5', '2', '1',
      '0.5', '0.2', '0.1', '0.05', '0.02', '0.01', '1', '1'),
    sum: null,
    transferUserLogin: null,
    transferUserName: null,
    prevCardBalances: 0,
    lastMoneyInCash: Array(),
    saveData: false,
    fileName: 'var/log/closePOS_TS.log',
    pause: 30 * 60, // seconds
    numberOfEntries: 16, // = facts.length - 1, wie im Legacy-Konstruktor
  }),
  // Das Legacy-prepare laeuft VOR create(): es holt die Zahl stornierter
  // Bons aus der DB und oeffnet die Lade. Rueckgabe ist ein Deferred.
  prepare(bridge) {
    return legacy.prepare.call(bridge);
  },
  // Nach dem Mount die Verweise herstellen, die frueher addEntities()
  // gesetzt hat: inputs[] und images[] je Eintrag, die Summenzeile, dazu die
  // XML-Namen und die controlFields des Zehnerblocks.
  mountLegacy(bridge) {
    bridge.nameComponent('0', 'titleContainer');
    // Die vier Kopfknoepfe brauchen ihre XML-Namen: der Ablauf schaltet sie
    // waehrend des Abschlusses ab und wieder frei
    // (titleContainer.components.okButton.setEnabled(...)).
    bridge.nameComponent('0:0', 'titleText');
    bridge.nameComponent('0:1', 'tempSaveButton');
    bridge.nameComponent('0:2', 'questButton');
    bridge.nameComponent('0:3', 'okButton');
    bridge.nameComponent('0:4', 'cancelButton');
    bridge.nameComponent('1', 'countContainerFrame');
    bridge.nameComponent('2', 'numPadContainer');
    bridge.nameComponent('2:0', 'numPad');
    bridge.nameComponent('3', 'transferContainer');
    bridge.nameComponent('3:3', 'pin');
    // cardNumber fehlte (Code-Suche 30.9.2026): getPersonalCard und okEnd
    // lesen/schreiben transferContainer.components.cardNumber - ohne Namen
    // war das undefined und die Kassa-Uebergabe unmoeglich. reDraw heisst
    // hier: den gescannten Wert (properties.value) anzeigen.
    bridge.nameComponent('3:2', 'cardNumber');
    const karte = bridge.handleAt('3:2');
    karte.reDraw = function () {
      bridge.update({ uebergabeKarte: karte.properties.value });
      return jQuery.Deferred().resolve().promise();
    };
    // Das PIN-Feld der Uebergabe braucht sein .field.input wie jedes andere
    // beschriftete Feld - es wird auch beim normalen Abschluss angefasst
    // (okEnd liest den PIN-Wert, bevor es ueberhaupt um eine Uebergabe geht).
    verdrahteLabeledInputField(bridge, '3:3');
    bridge.nameComponent('4', 'cancelBonsCaption');

    bridge.countContainer = bridge.handleAt('1:0');
    bridge.inputs = [];
    bridge.images = [];

    for (var i = 0; i < bridge.numberOfEntries; i++) {
      var c = Math.ceil((i + 1) / 9) * 2 - 1;
      var r = c === 1 ? i : i - 9;
      bridge.images.push(bridge.handleAt(`1:0:0:${r}:${c - 1}:0`));
      verdrahteLabeledInputField(bridge, `1:0:0:${r}:${c}:0`);
      bridge.inputs.push(bridge.handleAt(`1:0:0:${r}:${c}:0`));
    }

    // Die Summenzeile: sumValues() schreibt properties.value und ruft
    // reDraw(). Der Legacy-reDraw wuerde das Element ueber den Builder neu
    // bauen; hier schreibt er Inhalt UND Klasse direkt - genau das ist im
    // Legacy seine sichtbare Wirkung (der Builder haengt den Wert an die
    // Klasse, deshalb reicht reValue hier NICHT).
    bridge.sum = bridge.handleAt('1:0:0:7:3:0');
    bridge.sum.properties.value = float2formatString(0) + ' EUR';
    bridge.sum.reDraw = function () {
      const el = document.getElementById(this.id);
      if (el) {
        el.innerHTML = this.properties.value;
        el.className = 'CCaption ' + this.properties.value;
      }
      return jQuery.Deferred().resolve().promise();
    };

    // Der Zehnerblock schreibt in die ersten beiden Felder.
    bridge.components.numPadContainer.components.numPad.controlFields = [
      bridge.inputs[0].field.input,
      bridge.inputs[1].field.input,
    ];

    // Zwischengespeicherte Zaehlwerte einspielen (stand im Legacy am Ende
    // von addEntities).
    if ((bridge.mode == 'end' || bridge.mode == 'transfer') &&
      typeof POS.tempBalancingValues != 'undefined' && POS.tempBalancingValues.length > 0) {
      for (var n in bridge.inputs)
        bridge.inputs[n].field.input.properties.attributes.value = POS.tempBalancingValues[n];
      POS.tempBalancingValues = new Array();
      bridge.sumValues(bridge, true);
    }
  },
  legacyMethods: legacy,
});
